import React, { useCallback, useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import instance from "../../Api";
import { useAuth } from "../../AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../common/Loader";

// ─── Pure helpers ─────────────────────────────────────────────────────────────
const isMobileDevice = () => window.matchMedia("(max-width: 800px)").matches;

// ─── Main Component ───────────────────────────────────────────────────────────
const InvoicePage = () => {
  const { smuser }  = useAuth();
  const location    = useLocation();
  const navigate    = useNavigate();
  const billData    = location.state?.billData ?? null;
  const htmlContentRef = useRef(null);

  const [isMobile,     setIsMobile]     = useState(isMobileDevice);
  const [htmlContent,  setHtmlContent]  = useState("");
  const [loading,      setLoading]      = useState(true);

  // ── Fetch invoice HTML ────────────────────────────────────────────────────
  // Fix: original had [isMobile] as dep — caused the fetch (and navigate(-1) guard)
  // to re-fire on every window resize. Changed to [] — runs once on mount.
  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener("resize", handleResize);

    if (!billData) {
      navigate(-1);
    } else {
      (async () => {
        setLoading(true);
        try {
          const res = await instance.post("/invoiceview", { user_id: smuser.id, id: billData.id });
          setHtmlContent(res.data);
        } catch (e) {
          console.error("Error fetching invoice HTML:", e);
        }
        setLoading(false);
      })();
    }

    return () => window.removeEventListener("resize", handleResize);
  }, []); // ← run once; resize updates isMobile via listener

  // ── Shared: render ref content to a PDF blob ──────────────────────────────
  // generatePdf and printPdf were near-identical — 10 lines each duplicated.
  // Now one buildPdfBlob() covers both.
  const buildPdfBlob = useCallback(async () => {
    if (!htmlContentRef.current) return null;
    const canvas   = await html2canvas(htmlContentRef.current);
    const imgData  = canvas.toDataURL("image/png");
    const pdf      = new jsPDF("p", "mm", "a4");
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    return URL.createObjectURL(pdf.output("blob"));
  }, []);

  // ── Download ──────────────────────────────────────────────────────────────
  const generatePdf = useCallback(async () => {
    const url = await buildPdfBlob();
    if (!url) return;
    const link = document.createElement("a");
    link.href     = url;
    link.download = billData?.orderid ? `${billData.orderid}_invoice.pdf` : "file.pdf";
    link.click();
  }, [buildPdfBlob, billData]);

  // ── Print ─────────────────────────────────────────────────────────────────
  const printPdf = useCallback(async () => {
    const url = await buildPdfBlob();
    if (!url) return;
    const win = window.open(url);
    if (win) win.onload = () => win.print();
  }, [buildPdfBlob]);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <div className="heading-dotted-support" style={{ marginTop: isMobile ? "" : "3rem", marginLeft: isMobile ? "5%" : "15%" }}>
        PDF Preview <span />
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", justifyContent: isMobile ? "flex-start" : "flex-end", margin: isMobile ? "15px" : "0 15% 0px 0" }}>
        <button className="top-buttons-creact-machine" onClick={generatePdf} style={{ marginRight: "15px" }}>Download</button>
        <button className="top-buttons-creact-machine" onClick={printPdf}>Print</button>
      </div>

      {/* Invoice preview */}
      <div style={{ marginLeft: isMobile ? "5%" : "15%", marginRight: isMobile ? "5%" : "15%", paddingLeft: isMobile ? "0.5rem" : "20rem", border: "1px solid #ddd", borderRadius: "8px", backgroundColor: "#f3f3f3", boxShadow: "0 2px 10px rgba(0,0,0,0.1)", overflow: "auto", maxHeight: "90vh" }}>
        {htmlContent && <div ref={htmlContentRef} dangerouslySetInnerHTML={{ __html: htmlContent }} />}
      </div>

      {loading && (
        <div className="loading-overlay" style={{ zIndex: "9999999999999999" }}>
          <Loader isLoading={loading} />
        </div>
      )}
    </>
  );
};

export default InvoicePage;